/* eslint-disable */
import { CirclePlus } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  createCompliance_apiCalls,
  getDoctorCompliancesByAppointmentId_apiCalls,
  getPatientCompliancesByAppointmentId_apiCalls,
} from "../../api/api_calls/appointmentforms/compliance_apiCalls";
import { Button } from "../../components/ui/button";
import { Dialog, DialogTrigger } from "../../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { useToast } from "../../hooks/use-toast";
import ComplianceDialog from "../compliance-dialog/ComplianceDialog";
import ComplianceList from "../compliance-list/ComplianceList";

const PatientCompliance = () => {
  const [activeTab, setActiveTab] = useState("doctor");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [doctorNotes, setDoctorNotes] = useState([]);
  const [patientLogs, setPatientLogs] = useState([]);
  const [isLoading, setIsLoading] = useState({
    doctorNotes: false,
    patientLogs: false,
  });
  const user = useSelector((state) => state.userReducer.user);
  const location = useLocation();
  const { toast } = useToast();

  // Get appointmentId from location state
  const appointmentId =
    location.state?.appointmentId || location.state?.appointment?.id;

  // Unified function to fetch compliance data
  const fetchCompliances = async (type) => {
    if (!appointmentId) {
      return;
    }

    const isDoctor = type === "doctor";
    const loadingKey = isDoctor ? "doctorNotes" : "patientLogs";
    const apiCall = isDoctor
      ? getDoctorCompliancesByAppointmentId_apiCalls
      : getPatientCompliancesByAppointmentId_apiCalls;
    const setter = isDoctor ? setDoctorNotes : setPatientLogs;
    const errorMessage = `Failed to fetch ${isDoctor ? "doctor notes" : "patient logs"}. Please try again.`;

    try {
      setIsLoading((prev) => ({ ...prev, [loadingKey]: true }));
      const response = await apiCall(appointmentId);

      if (response?.data?.compliances) {
        const dataArray = Array.isArray(response.data.compliances)
          ? response.data.compliances
          : [];
        setter(dataArray);
      } else {
        setter([]);
      }
    } catch (error) {
      setter([]);
      if (
        error.message !==
        `No ${isDoctor ? "doctor" : "patient"} compliances found`
      ) {
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  // Interface functions to maintain existing component contracts
  const fetchDoctorNotes = () => fetchCompliances("doctor");
  const fetchPatientLogs = () => fetchCompliances("patient");

  // Fetch data when component mounts or tab changes
  useEffect(() => {
    if (activeTab === "doctor" && doctorNotes.length === 0) {
      fetchDoctorNotes();
    } else if (activeTab === "patients" && patientLogs.length === 0) {
      fetchPatientLogs();
    }
  }, [activeTab, appointmentId]);

  // Unified function to handle both doctor notes and patient logs
  const handleAddCompliance = async (data, type) => {
    if (!appointmentId) {
      toast({
        title: "Error",
        description: "No appointment ID provided",
        variant: "destructive",
      });
      return;
    }

    try {
      const complianceData = {
        appointmentId,
        type,
        log: data.log,
      };

      const response = await createCompliance_apiCalls(complianceData);

      if (response?.data) {
        if (type === "doctor") {
          setDoctorNotes((prev) => [response.data, ...prev]);
          setIsDialogOpen(false);
        } else {
          setPatientLogs((prev) => [response.data, ...prev]);
          setIsDialogOpen(false);
        }

        toast({
          title: "Success",
          description: `${type === "doctor" ? "Doctor note" : "Patient log"} added successfully`,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to add ${type === "doctor" ? "doctor note" : "patient log"}. Please try again.`,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-bold text-[20px] text-[#1E1E1E]">
          Patient Compliance
        </h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant={"primary"}
              size={"sm"}
              className="flex items-center gap-1"
            >
              <CirclePlus className="h-4 w-4" />
              <span>Add {user?.userType === "doctor" ? "Note" : "Log"}</span>
            </Button>
          </DialogTrigger>
          <ComplianceDialog
            onSubmit={(data) => handleAddCompliance(data, user?.userType)}
            type={user?.userType}
          />
        </Dialog>
      </div>

      <Tabs
        defaultValue="doctor"
        className="w-full"
        value={activeTab}
        onValueChange={setActiveTab}
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="doctor">Doctor&apos;s Notes</TabsTrigger>
          <TabsTrigger value="patients">Patient Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="doctor">
          <ComplianceList
            items={doctorNotes}
            isLoading={isLoading.doctorNotes}
            type="doctor"
          />
        </TabsContent>

        <TabsContent value="patients" className="relative">
          <ComplianceList
            items={patientLogs}
            isLoading={isLoading.patientLogs}
            type="patient"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PatientCompliance;
